import { acceptHMRUpdate, defineStore } from 'pinia'
import clevertap from 'clevertap-web-sdk'
import type { User } from '@/types'
import { getI18n } from '@/plugins/i18n.ts'
import { GrowthBook } from '@growthbook/growthbook'
import { autoAttributesPlugin, thirdPartyTrackingPlugin } from '@growthbook/growthbook/plugins'
import { useCookies } from '@vueuse/integrations/useCookies'


type EventDataValue = string | number | boolean | Date | null | undefined;
type EventData = Record<string, EventDataValue | EventDataValue[] | Record<string, EventDataValue>[]>;

export const useTrackStore = defineStore('track', () => {
  const gb = shallowRef<GrowthBook>()
  const subscriptionVersion = ref('current')

  const init = () => {
    clevertap.privacy.push({ optOut: false })
    clevertap.privacy.push({ useIP: true })
    clevertap.init(window.CLEVERTAP_PROJECT_ID)
    clevertap.spa = true
    clevertap.setLogLevel(import.meta.env.DEV ? 3 : 1)
  }


  const getGB = async () => {
    if (gb.value) {
      return gb.value
    }

    const getUUID = () => {
      let guuid = useCookies().get('gbuuid')
      if (guuid) {
        return guuid
      }
      guuid = window.crypto.randomUUID()
      useCookies().set('gbuuid', guuid, {
        path: '/',
        expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365),
      })
      return guuid
    }
    const localGB = new GrowthBook({
      apiHost: window.GROWTHBOOK_ENDPOINT,
      clientKey: window.GROWTHBOOK_CLIENT_KEY,
      enableDevMode: import.meta.env.DEV,
      attributes: {
        id: getUUID(),
        country: window.CURRENT_COUNTRY_ISO2,
      },
      plugins: [
        autoAttributesPlugin(),
        thirdPartyTrackingPlugin({ trackers: ['gtm'] }),
      ],
    })

    // Wait for features to be available
    await localGB.init({ streaming: false })
    gb.value = localGB
    return gb.value
  }

  const onLogin = (user: User) => {
    clevertap.onUserLogin.push({
      'Site': {
        'Name': user.name || undefined,
        'Identity': user.id,
        'Email': user.email || undefined,
        'Phone': user.phone ? `${user.phone}` : undefined,
        'Gender': user.gender ? (user.gender == 'male' ? 'M' : 'F') : undefined,
        'DOB': user.birthdate ? new Date(user.birthdate) : undefined,
        'MSG-email': true,
        'MSG-push': true,
        'MSG-sms': true,
        'MSG-whatsapp': true,
      },
    })
    window.clarity?.('identify', user.id + '')
  }

  const requestWebNotification = () => {
    setTimeout(function () {
      const t = getI18n().global.t
      clevertap.notifications.push({
        'titleText': t('web_push_notifications.title', 'Would you like to receive Push Notifications'),
        'bodyText': t('web_push_notifications.body', 'We promise to only send you relevant content and give you updates on your transactions'),
        'okButtonText': t('web_push_notifications.ok_button_text', 'Sign me up'),
        'rejectButtonText': t('web_push_notifications.reject_button_text', 'No thanks'),
        'okButtonColor': '#7366DB',
        'askAgainTimeInSeconds': 604800,
      })
    }, 10000)
  }

  const track = (name: string, eventData: EventData | undefined = undefined) => {
    clevertap.event.push(name, eventData || {})
    window.dataLayer.push({ 'event': name, properties: eventData || {} })
    window.clarity?.('event', name)
  }

  const trackAndGo = (url: string, name: string, eventData: EventData | undefined = undefined) => {
    track(name, eventData)
    window.open(url)
  }

  return {
    init,
    getGB,
    onLogin,
    track,
    trackAndGo,
    requestWebNotification,
    subscriptionVersion,
  }
})

// make sure to pass the right store definition, `useTrackStore` in this case.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTrackStore, import.meta.hot))
}
